<template>
  <div data-test="outside-vif">
    <template v-if="step === 1">
      <p>
        The following are results manually entered by users and not verified in
        the database. Adding a chemical not matched to the database has
        consequences for accuracy of regulatory reports.
      </p>
      <raft-busy v-if="isBusy" busy-text="Searching unlinked chemicals..." />
      <b-radio-group id="local_unlinked_search_results">
        <b-radio
          v-model="selectedLocalSearchResult"
          value="_none_"
          name="unlinked_chemicals"
          data-test="unlinked-none"
        >
          --None of the above--
        </b-radio>
        <b-radio
          v-for="option in localSearchResults"
          :key="option.id"
          :value="option.id"
          name="unlinked_chemicals"
          v-model="selectedLocalSearchResult"
        >
          {{ formatUnlinkedChemicalLabel(option) }}
        </b-radio>
      </b-radio-group>
    </template>
    <template v-if="step === 2">
      <p class="unlinked-chem-wiz-content">
        Are you sure this chemical is not listed under a different name or CAS
        number? Adding a chemical not matched to the database has consequences
        for the accuracy of regulatory reports.
      </p>
    </template>
    <template v-if="step === 3">
      <div id="unlinked-chemical-form">
        <b-form-group label="Add Unlinked Chemical" label-for="chemical-name">
          <b-form-row>
            <label
              id="chemical-name"
              for="chemical-name"
              class="required"
              title="This field is required."
              >Chemical name<sup>*</sup>:</label
            >
            <b-input type="text" name="chemical-name" v-model="chemicalName" />
          </b-form-row>
          <b-form-row>
            <label for="cas-number">CAS number:</label>
            <b-input type="text" name="cas-number" v-model="casNumber" />
          </b-form-row>
          <b-form-row>
            <label for="physical-state" class="required"
              >Physical state<sup>*</sup>:</label
            >
            <b-select
              name="physical-state"
              :options="physicalStates"
              v-model="physicalState"
            />
          </b-form-row>
        </b-form-group>
      </div>
    </template>

    <div id="unlinked-chemical-wizard-controls">
      <a v-if="step === 1" @click="$emit('cancel-add-unlinked-chemical')"
        >Return to chemical search</a
      >
      <b-button v-if="step > 1" @click="previousStep"> Back </b-button>
      <b-button v-if="step <= 3" @click="nextStep" data-test="nextButton">
        {{ nextButtonLabel }}
      </b-button>
      <raft-busy v-if="isBusy" busy-text="" />
    </div>
  </div>
</template>

<script>
import { jsonClient } from "@/lib/raftApi";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import RaftBusy from "../RaftBusy.vue";

const { config } = useDrupalSettings();
export default {
  name: "UnlinkedChemicalForm",
  components: {
    RaftBusy,
  },
  data() {
    return {
      isBusy: false,
      step: 1,
      chemicalName: "",
      casNumber: "",
      physicalState: "S",
      localSearchResults: [],
      selectedLocalSearchResult: null,
    };
  },
  props: {
    searchTerm: {
      type: String,
      required: true,
    },
  },
  methods: {
    handleLocalSearchResultSelection: function () {
      const constituent = this.localSearchResults.find(
        (result) => result.id === this.selectedLocalSearchResult,
      );
      constituent.isLocalUnlinkedChemical = true;
      this.$emit("unlinked-constituent-added", constituent);
    },
    handleAddUnlinkedChemical: async function () {
      this.isBusy = true;
      const body = {
        type: "node--chemdb_type",
        attributes: {
          title: this.chemicalName,
          revision_log: "Created from BioRAFT4",
          field_chemdb_cas_number: this.casNumber,
          field_chemdb_physical_state: this.physicalState,
        },
      };
      const options = {
        path: "node/chemdb_type",
        queries: {},
        data: body,
      };
      if (config.groupContext) {
        options.queries["rog-type"] = "node";
        options.queries["rog-id"] = config.groupContext;
      }
      /* eslint-disable no-console */
      const response = await jsonClient
        .post(options)
        .catch((error) => console.error(error));
      const constituent = {
        display: `${response?.deserialized?.title} (${
          config.physicalStates[
            response?.deserialized?.field_chemdb_physical_state
          ]
        })`,
        value: { 0: response?.deserialized?.id },
        type: response?.deserialized?.type,
        selectedObject: {
          selectedObject: {
            _source: {
              id: response?.deserialized?.id,
              uuid: [response?.deserialized?.id],
              title: [response?.deserialized?.title],
              field_chemdb_physical_state: [
                response?.deserialized?.field_chemdb_physical_state,
              ],
            },
            unlinked: true,
            type: response?.deserialized?.type,
          },
        },
        isLocalUnlinkedChemical: true,
        drupal_internal__nid: response?.deserialized?.drupal_internal__nid,
      };
      this.$emit("unlinked-constituent-added", constituent);
      this.isBusy = false;
    },
    nextStep: function () {
      if (
        this.selectedLocalSearchResult &&
        this.selectedLocalSearchResult !== "_none_"
      ) {
        this.handleLocalSearchResultSelection();
      } else if (this.step < 3) {
        this.step += 1;
      } else {
        this.handleAddUnlinkedChemical();
      }
    },
    previousStep: function () {
      if (this.step > 1) {
        this.step -= 1;
      }
    },
    searchLocalUnlinkedChemical: async function () {
      this.isBusy = true;
      const options = {
        path: "node/chemdb_type",
        queries: {
          "filter[status][condition][path]": "status",
          "filter[status][condition][value]": 1,
          "filter[status][condition][operator]": "=",
          "filter[chem-gdn][condition][path]": "field_chemdb_gdn",
          "filter[chem-gdn][condition][value]": "0",
          "filter[chem-gdn][condition][operator]": "=",
          "fields[node--chemdb_type]":
            "drupal_internal__nid,title,field_chemdb_cas_number,field_chemdb_physical_state,field_chemdb_gdn",
          "filter[or-group][group][conjunction]": "OR",
          "filter[chem-name][condition][path]": "title",
          "filter[chem-name][condition][value]": this.trimmedSearchTerm,
          "filter[chem-name][condition][operator]": "CONTAINS",
          "filter[chem-name][condition][memberOf]": "or-group",
          "filter[chem-cas][condition][path]": "field_chemdb_cas_number",
          "filter[chem-cas][condition][value]": this.trimmedSearchTerm,
          "filter[chem-cas][condition][operator]": "STARTS_WITH",
          "filter[chem-cas][condition][memberOf]": "or-group",
        },
      };
      /* eslint-disable no-console */
      const results = await jsonClient
        .get(options)
        .catch((error) => console.error(error));
      this.localSearchResults = results?.deserialized;
      this.isBusy = false;
    },
    formatUnlinkedChemicalLabel: function (item) {
      const state = config.physicalStates[item.field_chemdb_physical_state];
      return item.title + " (" + state + ")";
    },
  },
  computed: {
    trimmedSearchTerm() {
      return this.searchTerm ? this.searchTerm.trim() : "missing search term";
    },
    physicalStates: function () {
      return config.physicalStates;
    },
    nextButtonLabel: function () {
      if (this.step === 3) {
        return "Add unlinked chemical";
      }
      return "Continue";
    },
  },
  created() {
    this.chemicalName = this.searchTerm;
  },
  watch: {
    step: {
      immediate: true,
      handler: function (newValue) {
        if (newValue === 1) {
          this.searchLocalUnlinkedChemical();
        }
      },
    },
  },
};
</script>

<style scoped>
#local_unlinked_search_results :deep(label) {
  display: inline-block;
  font-weight: normal;
  margin-left: 0.125rem;
}
#unlinked-chemical-wizard-controls {
  margin-top: 1rem;
}
#unlinked-chemical-form :deep(.form-group) {
  display: flex;
  flex-flow: row wrap;
  margin: 0.5rem;
}
#unlinked-chemical-form :deep(.form-row) {
  flex: 0 1 100%;
  margin-bottom: 0.25rem;
}

#unlinked-chemical-form :deep(input) {
  display: block;
  width: 100%;
}

#unlinked-chemical-form :deep(label:first-child) {
  margin-left: 0;
}
#unlinked-chemical-form :deep(label) {
  font-weight: bold;
}

#unlinked-chemical-form :deep(label),
#unlinked-chemical-form :deep(select) {
  display: block;
  width: 100%;
  max-width: 100%;
}
</style>
